import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import config from 'constants/config'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'components/Luxkit/TextLink'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import moment from 'moment'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextButton from 'components/Luxkit/Button/TextButton'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import getBedbankPromoExtraValue, { getDisplayTotalPricePerNight, getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import Group from 'components/utils/Group'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import { pluralizeToString } from 'lib/string/pluralize'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowFlightPackage from 'components/Luxkit/PricePoints/PriceRowFlightPackage'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import AccommodationPackageTilePricingBlocks from '../Accommodation/AccommodationPackageTilePricingBlocks'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { usePageViewAnalyticsPriceShare } from 'hooks/Analytics/usePageViewAnalyticsPriceShare'

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(4)};
`

const FlightPrice = styled(PriceDetails)`
  margin-top: ${rem(8)};
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  text-align: center;
`

interface Props {
  rate?: App.BedbankRate;
  bundleRate?: App.BedbankRate;
  rooms: Array<App.Occupants>;
  offer: App.BedbankOffer;
  flightOrigin?: App.Airport;
  flightPrice?: number;
  duration: number;
  checkIn?: string;
  departureTooEarly?: boolean;
  flightsEnabled: boolean;
  onViewPackageOptions?: () => void;
  promotion?: boolean
  alignment?: 'center' | 'start'
  luxPlusLPPEnabled: boolean
}

function BedbankSummaryPriceDetails(props: Props) {
  const {
    rate,
    bundleRate,
    rooms,
    offer,
    checkIn,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    duration,
    onViewPackageOptions,
    promotion = false,
    alignment = 'center',
    luxPlusLPPEnabled,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()
  const hasFlights = !!flightPrice
  const sell = useBedbankOfferSell(offer, duration, rooms)

  const checkOut = useMemo(() => checkIn ? moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, duration])
  const promo = useBedbankRoomRatePromotions(offer.promotions, checkIn, checkOut)

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch])

  const taxesAndFees = rate?.totals?.taxesAndFees ?? sell?.taxesAndFees ?? 0
  const propertyFees = rate?.totals?.propertyFees ?? sell?.propertyFees ?? 0
  const bundleTaxesAndFees = bundleRate?.totals.taxesAndFees || taxesAndFees
  const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees

  const hotelPrice = rate ? rate.totals.inclusive : sell?.price ?? 0
  const hotelMemberPrice = rate?.totals.memberInclusive ?? 0
  const bundlePrice = bundleRate?.totals.inclusive || hotelPrice
  const bundleMemberPrice = bundleRate?.totals.memberInclusive || hotelMemberPrice
  let hotelValue: undefined | number
  let bundleValue: undefined | number

  let hotelDiscount: number
  let hotelMemberDiscount: number = 0

  if (rate) {
    const pkg = rate.room
    const isPromoEnabledRate = !!promo[rate.id]

    if (isPromoEnabledRate) {
      const inclusionsAmount = pkg?.sell ? getBedbankPromoExtraValue({
        price: pkg.sell.price,
        value: pkg.sell.value,
        minLos: pkg.sell.los,
        duration,
      }) : 0
      hotelValue = inclusionsAmount + rate.totals.inclusive
      bundleValue = inclusionsAmount + (bundleRate?.totals.inclusive || hotelValue)
      hotelDiscount = getValueOffPercent(hotelValue, inclusionsAmount)
    } else {
      hotelValue = rate.value
      bundleValue = bundleRate?.value || hotelValue
      hotelDiscount = rate.discount
      hotelMemberDiscount = rate.memberDiscount
    }
  } else {
    hotelValue = sell?.value
    bundleValue = sell?.value
    hotelDiscount = sell?.discountPercentage ?? 0
  }

  const hotelNights = rate?.nights ?? sell?.los

  const departureTooFarInFuture = useMemo(() => checkIn ? moment(checkIn).diff(moment.utc(), 'days') > 330 : false, [checkIn])

  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'room'
  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle

  const baseTotalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue,
    hotelDiscount,
    hotelMemberDiscount,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const totalPricesPerNight = getDisplayTotalPricePerNight(baseTotalPrices, hotelNights || 1)
  const showOneNightPrice = !promotion && !offer.displayPricingAsPerPerson && !checkIn && !!hotelNights && hotelNights > 1
  const totalPrices = showOneNightPrice ? totalPricesPerNight : baseTotalPrices
  const nightsToDisplay = showOneNightPrice ? 1 : hotelNights

  const offerLocations: Array<string> = useMemo(() => {
    const locations = [
      offer.property?.address.city,
      offer.property?.address.stateProvinceName || '',
      offer.property?.address.countryName]
      .filter(Boolean)

    return locations
  }, [offer])

  const discount = promoteAsBundle ? totalPrices.original.discountWithFlight : totalPrices.original.discount
  const memberDiscount = promoteAsBundle ? totalPrices.original.memberDiscountWithFlight : totalPrices.original.memberDiscount
  const displayDiscount = useBedbankDiscount(discount, memberDiscount)

  useEffect(() => {
    appDispatch(setCustomerValue(totalPrices.totals.price))
  }, [appDispatch, totalPrices.totals.price])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorBedbankOptions(rate, {
      price: totalPrices.totals.price,
      memberPrice: totalPrices.totals.memberPrice,
      propertyFees,
    }),
  ], [totalPrices.totals.memberPrice, totalPrices.totals.price, rate, propertyFees])

  const showHotelPriceWithFlights = !!flightsEnabled && hasFlights && !offer.promoteAsBundle

  const analyticsData = useMemo(() => {
    if (showHotelPriceWithFlights) {
      return {
        price: totalPrices.totals.price,
      }
    }

    return {
      price: promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price,
    }
  }, [showHotelPriceWithFlights, totalPrices, promoteAsBundle])

  usePageViewAnalyticsPriceShare(analyticsData)

  if (showHotelPriceWithFlights) {
    return <Group direction="vertical" fullWidth gap={16} horizontalAlign="center">
      {displayDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
        type="estimate"
        discountPercentage={displayDiscount}
      />}
      <AccommodationPackageTilePricingBlocks>
        <Group direction="vertical" gap={12} verticalAlign="space-between">
          <Group gap={8} direction="vertical" verticalAlign="space-evenly">
            <Group direction="vertical" gap={12}>
              <BodyText variant="large" weight="bold">Hotel</BodyText>
              <Group direction="vertical">
                <Group direction="vertical" gap={4}>
                  <Group direction="vertical">
                    {!!nightsToDisplay && <PriceRowPriceCaption>
                      {pluralizeToString('night', nightsToDisplay)} from
                    </PriceRowPriceCaption>}
                    <LuxPlusPriceGuard
                      showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                      fallbackPrice={<PriceRowPrice
                        size="L"
                        price={totalPrices.totals.price}
                        saleUnit={saleUnit}
                      />}
                    >
                      <LuxPlusPriceStack
                        size="S"
                        price={totalPrices.totals.price}
                        memberPrice={totalPrices.totals.memberPrice}
                        saleUnit={saleUnit}
                        leadWithLuxPlusPrice
                      />
                    </LuxPlusPriceGuard>
                    {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
                  </Group>
                  {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                    type="exact"
                    offer={offer}
                    totalCost={baseTotalPrices.original.price}
                    numberOfNights={hotelNights}
                    roomCount={rooms.length}
                  />}
                </Group>
                {taxesAndFees > 0 && <PriceRowTaxesAndFees
                  endIcon={<OfferPriceDetailsRow
                    offerPage
                    offer={offer}
                    rooms={rooms}
                    propertyFees={propertyFees}
                    hotelPrice={hotelPrice}
                    hotelMemberPrice={hotelMemberPrice}
                    taxesAndFees={taxesAndFees}
                    flightPrice={flightPrice}
                    showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
                    duration={hotelNights ?? 0}
                    trigger="info-icon"
                    cta={<TextButton kind="primary" fit="flex">
                      View {config.brandConfig.travelSynonym} options
                    </TextButton>}
                    onCtaAfterClose={onViewPackageOptions}
                  />}
                />}
                <PriceRowAgentHubCommission
                  size="L"
                  productType={offer.productType}
                  offerLocations={offerLocations}
                  offerId={offer.id}
                />
                {!!displayDiscount && !!hotelValue && <PriceRowValueDiscountWithCaption
                  size="L"
                  originalValue={totalPrices.totals.value}
                />}
              </Group>
            </Group>
          </Group>
          <TextButton
            kind="secondary"
            data-testid="OfferAvailable"
            onClick={onViewPackageOptions}
            fit="flex"
          >
            View options
          </TextButton>
        </Group>
        <Group direction="vertical" gap={12} verticalAlign="space-between">
          <Group gap={8} direction="vertical" verticalAlign="space-evenly">
            <Group direction="vertical" gap={12} verticalAlign="space-evenly">
              <BodyTextBlock startIcon={<LinePlaneDepartureIcon />} variant="large" weight="bold">Hotel + flights</BodyTextBlock>
              <Group direction="vertical">
                <Group direction="vertical" gap={4}>
                  <Group direction="vertical">
                    {!!nightsToDisplay && <PriceRowPriceCaption>
                      {pluralizeToString('night', nightsToDisplay)} + flights from
                    </PriceRowPriceCaption>}
                    <LuxPlusPriceGuard
                      showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                      fallbackPrice={<PriceRowPrice
                        size="L"
                        price={totalPrices.totals.flightBundlePrice}
                        saleUnit={saleUnit}
                      />}
                    >
                      <LuxPlusPriceStack
                        size="S"
                        price={totalPrices.totals.flightBundlePrice}
                        memberPrice={totalPrices.totals.flightBundleMemberPrice}
                        saleUnit={saleUnit}
                        leadWithLuxPlusPrice
                      />
                    </LuxPlusPriceGuard>
                    {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
                  </Group>
                  {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                    type="exact"
                    offer={offer}
                    totalCost={baseTotalPrices.original.flightBundlePrice}
                    numberOfNights={hotelNights}
                    roomCount={rooms.length}
                  />}
                </Group>
                {taxesAndFees > 0 && <PriceRowTaxesAndFees
                  endIcon={<OfferPriceDetailsRow
                    offerPage
                    offer={offer}
                    rooms={rooms}
                    propertyFees={bundlePropertyFees}
                    hotelPrice={bundlePrice}
                    hotelMemberPrice={bundleMemberPrice}
                    taxesAndFees={bundleTaxesAndFees}
                    flightPrice={flightPrice}
                    showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
                    duration={hotelNights ?? 0}
                    trigger="info-icon"
                    cta={<TextButton kind="primary" fit="flex">
                      View {config.brandConfig.travelSynonym} options
                    </TextButton>}
                    onCtaAfterClose={onViewPackageOptions}
                  />}
                />}
                <PriceRowAgentHubCommission
                  size="L"
                  productType={offer.productType}
                  offerLocations={offerLocations}
                  offerId={offer.id}
                />
                {!!displayDiscount && !!hotelValue && <PriceRowValueDiscountWithCaption
                  size="L"
                  originalValue={totalPrices.totals.flightBundleValue}
                />}
              </Group>
            </Group>
          </Group>
          <TextButton
            kind="secondary"
            data-testid="OfferAvailable"
            onClick={onViewPackageOptions}
            fit="flex"
          >
            View options
          </TextButton>
        </Group>
      </AccommodationPackageTilePricingBlocks>
      <LoyaltyPoints calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
    </Group>
  }

  return (
    <Group direction="vertical" horizontalAlign={alignment}>
      <Group direction="vertical" horizontalAlign={alignment} gap={2}>
        <Group direction="vertical" horizontalAlign={alignment} gap={12}>
          <Group direction="vertical" horizontalAlign={alignment}>
            <Group direction="vertical" horizontalAlign={alignment} gap={4}>
              {displayDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
                type="estimate"
                discountPercentage={displayDiscount}
              />}
              <Group direction="vertical" horizontalAlign={alignment}>
                {!!nightsToDisplay && <PriceRowPriceCaption>
                  {pluralizeToString('night', nightsToDisplay)}{promoteAsBundle && ' + flights'} from
                </PriceRowPriceCaption>}
                <LuxPlusPriceGuard
                  showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                  fallbackPrice={<PriceRowPrice
                    size="L"
                    price={promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price}
                    saleUnit={saleUnit}
                  />}
                >
                  <LuxPlusPriceStack
                    horizontalAlign={alignment}
                    size="L"
                    price={promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price}
                    memberPrice={promoteAsBundle ? totalPrices.totals.flightBundleMemberPrice : totalPrices.totals.memberPrice}
                    saleUnit={saleUnit}
                    leadWithLuxPlusPrice
                  />
                </LuxPlusPriceGuard>
                {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
              </Group>
              {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                type="exact"
                offer={offer}
                totalCost={promoteAsBundle ? baseTotalPrices.original.flightBundlePrice : baseTotalPrices.original.price}
                numberOfNights={hotelNights}
                roomCount={rooms.length}
              />}
            </Group>
            {taxesAndFees > 0 && <PriceRowTaxesAndFees
              endIcon={<OfferPriceDetailsRow
                offerPage
                offer={offer}
                rooms={rooms}
                propertyFees={promoteAsBundle ? bundlePropertyFees : propertyFees}
                hotelPrice={promoteAsBundle ? bundlePrice : hotelPrice}
                hotelMemberPrice={promoteAsBundle ? bundleMemberPrice : hotelMemberPrice}
                taxesAndFees={promoteAsBundle ? bundleTaxesAndFees : taxesAndFees}
                flightPrice={promoteAsBundle ? flightPrice : undefined}
                showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
                duration={hotelNights ?? 0}
                trigger="info-icon"
                cta={<TextButton kind="primary" fit="flex">
                  View {config.brandConfig.travelSynonym} options
                </TextButton>}
                onCtaAfterClose={onViewPackageOptions}
              />}
            />}
            <PriceRowAgentHubCommission
              size="L"
              productType={offer.productType}
              offerLocations={offerLocations}
              offerId={offer.id}
            />
            {!!displayDiscount && !!hotelValue && <PriceRowValueDiscountWithCaption
              size="L"
              originalValue={promoteAsBundle ? totalPrices.totals.flightBundleValue : totalPrices.totals.value}
            />}
          </Group>
          <LoyaltyPoints calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
        </Group>
        {flightsEnabled && !promoteAsBundle &&
          <FlightPrice>
            {!hasFlights && flightOrigin && flightOrigin.code === offer.flights?.airportCode &&
              <ChangeDepartureWrapper>
                <PriceRowCaption>
                  No flights available from {flightOrigin.name}. Please select another airport.
                </PriceRowCaption>
                <TextLink
                  onClick={onToggleFilter}
                  data-filter="airport"
                >
                  Change departure airport
                </TextLink>
              </ChangeDepartureWrapper>
          }
            {!hasFlights && flightOrigin?.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly &&
              <PriceRowCaption>
                Add flights to the hotel package. Select from all major airlines at the next step.
              </PriceRowCaption>
          }
            {!hasFlights && departureTooFarInFuture &&
              <PriceRowCaption>
                Flights can only be booked up to 11 months in advance.
              </PriceRowCaption>
          }
            {flightOrigin?.code !== offer.flights?.airportCode && departureTooEarly &&
              <ChangeDepartureWrapper>
                <BodyText variant="small" colour="neutral-two">
                  Flights cannot be booked online when check-in is within 24 hours
                </BodyText>
                <TextLink
                  onClick={onToggleFilter}
                  data-filter="checkin"
                >
                  Change departure airport
                </TextLink>
              </ChangeDepartureWrapper>
          }
            {hasFlights && !departureTooEarly &&
              <Group direction="vertical" horizontalAlign="center">
                <PriceRowFlightPackage
                  product="hotel"
                  price={totalPrices.totals.flightBundlePrice}
                  pricePerPerson={offer.displayPricingAsPerPerson}
                />
              </Group>
            }
            <AtolProtectedTooltip />
          </FlightPrice>
        }
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
}), BedbankSummaryPriceDetails)
